/**
 * Inline Meeting Tool detail panel (placeholder for tools not yet live).
 *
 * Most tools scroll in the panel body. Chat fills the panel and scrolls
 * internally so the message composer stays sticky at the bottom.
 */

import React from "react";
import { ChevronLeft, X } from "lucide-react";
import { useScrollContainerWheel } from "../../utils/useScrollContainerWheel";
import type { MeetingToolId } from "./meetingToolSections";
import {
  MeetingToolsResizeHandle,
} from "./useMeetingToolsResize";

function toolLabel(toolId: MeetingToolId): string {
  switch (toolId) {
    case "quiz":
      return "Quizzes";
    case "resources":
      return "Resources";
    case "progress":
      return "Progress";
    case "qa":
      return "Q&A";
    case "announcements":
      return "Announcements";
    case "polls":
      return "Polls";
    case "feedback":
      return "Feedback";
    case "chat":
      return "Class Chat";
    case "participants":
      return "Participants";
    case "engagement":
      return "Attendance & Engagement";
    case "distraction":
      return "Distraction Report";
    case "absorption":
      return "Topic Absorption";
    case "quiz-scores":
      return "Quiz Scores";
    case "session-topics":
      return "Session Topics";
    case "manage-topic":
      return "Manage Topic";
    case "timer":
      return "Timer";
    case "recording":
      return "Recording";
    case "captions":
      return "Captions";
    case "network":
      return "Network Status";
    case "settings":
      return "Settings";
    default:
      return "Tool";
  }
}

/** Tools with their own sticky footer (composer) — fill height, no outer scroll. */
const FILL_HEIGHT_TOOLS = new Set<MeetingToolId>(["chat"]);

export function MeetingToolPanel({
  toolId,
  message,
  onBack,
  children,
  width,
  onResizePointerDown,
  mobile = false,
}: {
  toolId: MeetingToolId;
  message?: string;
  onBack: () => void;
  children?: React.ReactNode;
  /** Shared width from LiveSessionRoom (persists across tool open/back). */
  width: number;
  onResizePointerDown: (e: React.PointerEvent) => void;
  /** Full-screen sheet overlay for narrow viewports. */
  mobile?: boolean;
}) {
  const label = toolLabel(toolId);
  const scrollRef = useScrollContainerWheel<HTMLDivElement>();
  const fillHeight = FILL_HEIGHT_TOOLS.has(toolId);

  return (
    <aside
      style={
        mobile
          ? {
              position: "fixed",
              inset: 0,
              zIndex: 1250,
              width: "100%",
              maxWidth: "100%",
              height: "100%",
              maxHeight: "100dvh",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              background: "#141820",
              boxSizing: "border-box",
              paddingBottom: "env(safe-area-inset-bottom, 0px)",
            }
          : {
              position: "relative",
              flexShrink: 0,
              width,
              maxWidth: "45vw",
              height: "100%",
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              borderLeft: "1px solid rgba(255,255,255,0.1)",
              background: "#141820",
              boxShadow: "-12px 0 40px rgba(0,0,0,0.35)",
            }
      }
    >
      {mobile ? null : (
        <MeetingToolsResizeHandle onPointerDown={onResizePointerDown} />
      )}
      <div
        style={{
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          padding: mobile ? "14px 16px" : "12px 14px",
          borderBottom: "1px solid rgba(255,255,255,0.1)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
          {mobile ? null : (
            <button
              type="button"
              onClick={onBack}
              aria-label="Back to Meeting Tools"
              style={{
                border: "none",
                background: "transparent",
                color: "#94a3b8",
                cursor: "pointer",
                padding: 4,
                lineHeight: 0,
              }}
            >
              <ChevronLeft size={18} />
            </button>
          )}
          <h2
            style={{
              margin: 0,
              fontSize: mobile ? 16 : 14,
              fontWeight: 700,
              color: "#fff",
            }}
          >
            {label}
          </h2>
        </div>
        {mobile ? (
          <button
            type="button"
            onClick={onBack}
            aria-label={`Close ${label}`}
            style={{
              border: "none",
              background: "transparent",
              color: "#94a3b8",
              cursor: "pointer",
              padding: 4,
              lineHeight: 0,
            }}
          >
            <X size={18} />
          </button>
        ) : null}
      </div>
      <div
        ref={scrollRef}
        className="meeting-tool-scroll-body"
        style={{
          flex: 1,
          minHeight: 0,
          height: 0,
          overflowY: fillHeight ? "hidden" : "auto",
          overflowX: "hidden",
          display: "flex",
          flexDirection: "column",
          padding: 16,
          color: "#94a3b8",
          fontSize: 13,
          lineHeight: 1.5,
          WebkitOverflowScrolling: "touch",
          overscrollBehavior: "contain",
          touchAction: "pan-y",
        }}
      >
        <style>{`
          .meeting-tool-scroll-body {
            scrollbar-width: thin;
            scrollbar-color: rgba(148,163,184,0.55) transparent;
            -webkit-overflow-scrolling: touch;
          }
          .meeting-tool-scroll-body::-webkit-scrollbar {
            display: block;
            width: 8px;
          }
          .meeting-tool-scroll-body::-webkit-scrollbar-thumb {
            background: rgba(148,163,184,0.55);
            border-radius: 999px;
          }
          .meeting-tool-scroll-body::-webkit-scrollbar-track {
            background: transparent;
          }
        `}</style>
        {children ? (
          <div
            style={
              fillHeight
                ? {
                    flex: 1,
                    minHeight: 0,
                    width: "100%",
                    height: "100%",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                  }
                : { flex: "0 0 auto", width: "100%", minHeight: "100%" }
            }
          >
            {children}
          </div>
        ) : (
          <p style={{ margin: 0 }}>
            {message ||
              "This meeting tool will be available in a later update."}
          </p>
        )}
      </div>
    </aside>
  );
}
